import type { FC } from 'react'
import React, { useContext, useState } from 'react'
import styled from 'styled-components'

import H6 from '@/components/H6'
import LoadingSpinner from '@/components/LoadingSpinner'
import SmallPill from '@/components/SmallPill'
import ctx from '@/context/AppContext'
import { useCancelDocumentRequestMutation } from '@/lib/apollo'

interface Props {
  applicationId: string
  customerId: string
  subjectId: string
  recommendationId: string
}

type Stage = 'idle' | 'confirm'

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: min-content min-content;
  grid-column-gap: 10px;
  justify-content: end;
`

const CancelRequest: FC<Props> = ({
  applicationId,
  customerId,
  subjectId,
  recommendationId,
}) => {
  const [stage, setStage] = useState<Stage>('idle')
  const [notifyApplicant, setNotifyApplicant] = useState<boolean>(false)

  const { personId } = useContext(ctx)

  const [performCancelDocumentRequest, { loading, error, data }] =
    useCancelDocumentRequestMutation()

  const performCancel = () =>
    performCancelDocumentRequest({
      variables: {
        applicationId,
        customerId,
        subjectId,
        userId: personId ?? '',
        recommendationId,
        notifyApplicant,
      },
    })

  return (
    <>
      {stage === 'idle' ? (
        <SmallPill type="button" onClick={() => setStage('confirm')} styleType="danger">
          Cancel
        </SmallPill>
      ) : (
        <>
          {loading ? (
            <LoadingSpinner />
          ) : data ? (
            <p>The document request was cancelled.</p>
          ) : (
            <>
              <H6>Please confirm</H6>
              <em>Are you sure you would like to cancel this request?</em>
              <br />
              <div
                className="side-by-side-flex-start"
                onClick={() => setNotifyApplicant(!notifyApplicant)}
              >
                <input type="checkbox" checked={notifyApplicant} />
                <p>
                  Yes and inform the applicant we have cancelled this document request
                </p>
              </div>
              {error && <p>An unexpected error occurred.</p>}
              <ButtonContainer>
                <SmallPill onClick={() => setStage('idle')}>Back</SmallPill>
                <SmallPill
                  type="button"
                  disabled={loading}
                  onClick={performCancel}
                  styleType="danger"
                >
                  Confirm
                </SmallPill>
              </ButtonContainer>
            </>
          )}
        </>
      )}
    </>
  )
}

export default CancelRequest
